import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=not auth, object=auth
  const [pinVerified, setPinVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true, headers });
      setUser(res.data);
      const pinOk = sessionStorage.getItem("pin_verified") === "true";
      setPinVerified(pinOk);
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.access_token) {
        localStorage.setItem("access_token", res.data.access_token);
        localStorage.setItem("refresh_token", res.data.refresh_token);
      }
      setUser(res.data);
      setPinVerified(false);
      sessionStorage.removeItem("pin_verified");
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, pin) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/register`, { email, password, name, pin }, { withCredentials: true });
      if (res.data.access_token) {
        localStorage.setItem("access_token", res.data.access_token);
        localStorage.setItem("refresh_token", res.data.refresh_token);
      }
      setUser(res.data);
      setPinVerified(false);
      sessionStorage.removeItem("pin_verified");
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyPin = () => {
    setPinVerified(true);
    sessionStorage.setItem("pin_verified", "true");
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true, headers });
    } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setUser(false);
    setPinVerified(false);
    sessionStorage.removeItem("pin_verified");
  };

  return (
    <AuthContext.Provider value={{ user, loading, pinVerified, login, register, logout, verifyPin }}>
      {children}
    </AuthContext.Provider>
  );
}
